import { Checkbox, Flex, Form, InputNumber, Radio as AntdRadio } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import styles from "./ProgressiveDurationPricing.module.css"

export const PROGRESSIVE_PRICING_DIRECTION = {
  INCREASE: "increase",
  DECREASE: "decrease",
}

export const PROGRESSIVE_PRICING_UNIT = {
  PERCENT: "percent",
  FIXED: "fixed",
}

export const ProgressiveDurationPricing = ({ form }) => {
  const { t } = useTranslation()
  const isEnabled = Form.useWatch("progressive_pricing_enabled", form)

  return (
    <Flex vertical gap={spacings.M}>
      <Form.Item name="progressive_pricing_enabled" valuePropName="checked" className="mb-0">
        <Checkbox>
          <Text fw={500} size="m">
            {t("experiences.pricing.private.progressive_pricing_enabled")}
          </Text>
        </Checkbox>
      </Form.Item>

      {isEnabled && (
        <Flex vertical gap={spacings.L} className={styles.section}>
          {/* Apply threshold */}
          <Flex align="center" gap={spacings.S} wrap="wrap">
            <Text size="m">
              {t("experiences.pricing.private.progressive_pricing_threshold_label")}
            </Text>
            <Form.Item
              name="progressive_pricing_threshold"
              className="mb-0"
              rules={[{ required: true, message: t("form_validation.cannot_be_blank") }]}
            >
              <InputNumber
                size="large"
                min={1}
                addonAfter={t("experiences.pricing.private.days")}
                className={styles.daysInput}
              />
            </Form.Item>
          </Flex>

          {/* Increase / Decrease direction */}
          <Form.Item
            name="progressive_pricing_direction"
            initialValue={PROGRESSIVE_PRICING_DIRECTION.DECREASE}
            className="mb-0"
          >
            <AntdRadio.Group>
              <AntdRadio value={PROGRESSIVE_PRICING_DIRECTION.INCREASE}>
                {t("experiences.pricing.private.progressive_pricing_increase")}
              </AntdRadio>
              <AntdRadio value={PROGRESSIVE_PRICING_DIRECTION.DECREASE}>
                {t("experiences.pricing.private.progressive_pricing_decrease")}
              </AntdRadio>
            </AntdRadio.Group>
          </Form.Item>

          {/* Amount + unit toggle + per-day label */}
          <Flex vertical gap={spacings.XS}>
            <Flex align="center" gap={spacings.S} wrap="wrap">
              <Form.Item
                name="progressive_pricing_value"
                className="mb-0"
                dependencies={["progressive_pricing_direction", "progressive_pricing_unit"]}
                validateFirst
                rules={[
                  { required: true, message: t("form_validation.cannot_be_blank") },
                  // A decrease can't take off more than the whole day's rate; an increase has no cap (QA #19).
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const isPercentDecrease =
                        getFieldValue("progressive_pricing_direction") === PROGRESSIVE_PRICING_DIRECTION.DECREASE &&
                        getFieldValue("progressive_pricing_unit") === PROGRESSIVE_PRICING_UNIT.PERCENT
                      if (isPercentDecrease && value > 100) {
                        return Promise.reject(new Error(t("experiences.pricing.private.progressive_pricing_max_percent")))
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <InputNumber size="large" min={0} className={styles.valueInput} />
              </Form.Item>
              <Form.Item
                name="progressive_pricing_unit"
                initialValue={PROGRESSIVE_PRICING_UNIT.PERCENT}
                className="mb-0"
              >
                <AntdRadio.Group optionType="button" buttonStyle="solid">
                  <AntdRadio.Button value={PROGRESSIVE_PRICING_UNIT.PERCENT}>%</AntdRadio.Button>
                  <AntdRadio.Button value={PROGRESSIVE_PRICING_UNIT.FIXED}>$</AntdRadio.Button>
                </AntdRadio.Group>
              </Form.Item>
              <Text size="m">
                {t("experiences.pricing.private.progressive_pricing_per_day_label")}
              </Text>
            </Flex>

            <Flex align="center" gap={spacings.XS} className={styles.infoRow}>
              <InfoCircleOutlined className={styles.infoIcon} />
              <Text size="m" color="var(--color-text-secondary)">
                {t("experiences.pricing.private.progressive_pricing_applies_to")}
              </Text>
            </Flex>
          </Flex>

          {/* Stop applying after N days */}
          <Flex align="center" gap={spacings.S} wrap="wrap">
            <Text size="m">
              {t("experiences.pricing.private.progressive_pricing_cap_label")}
            </Text>
            <Form.Item
              name="progressive_pricing_cap"
              className="mb-0"
              dependencies={["progressive_pricing_threshold"]}
              validateFirst
              rules={[
                { required: true, message: t("form_validation.cannot_be_blank") },
                // The pricing applies to days after the threshold up to the cap, so a cap at or below the
                // threshold never applies (QA #20).
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const threshold = getFieldValue("progressive_pricing_threshold")
                    if (threshold && value <= threshold) {
                      return Promise.reject(new Error(t("experiences.pricing.private.progressive_pricing_cap_after_threshold", { count: threshold })))
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <InputNumber
                size="large"
                min={1}
                addonAfter={t("experiences.pricing.private.days")}
                className={styles.daysInput}
              />
            </Form.Item>
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}

ProgressiveDurationPricing.propTypes = {
  form: PropTypes.object.isRequired,
}
